/*
 * CORE API CLIENT
 * ===============
 * This is the lowest-level browser helper for JSON API calls.
 *
 * In plain English:
 * - Other frontend services hand this file a URL path and some options.
 * - This file builds the real request, sends it, and turns the response
 *   into a normal JavaScript object.
 * - If the API returns an error, this file converts it into a readable
 *   Error object so page components can show a useful message.
 *
 * Keep this file generic. Business rules belong in higher-level services
 * such as `azureSqlService.js`, not here.
 */
import { getCurrentReturnTo, tryStandbyReauth } from "@/utils/standbySwaReauth";

export type ApiRequestOptions = {
  method?: string;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
};

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

// Low-level API helper that sends a request and turns the response into a useful error if it fails.
export class CoreApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: FetchLike;

  constructor(baseUrl: string, fetchImpl: FetchLike) {
    this.baseUrl = baseUrl;
    this.fetchImpl = fetchImpl;
  }

  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    // Build the final URL and attach any query parameters the caller passed in.
    const url = new URL(path, window.location.origin);
    Object.entries(options.query || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && `${value}`.trim() !== "") {
        url.searchParams.append(key, `${value}`);
      }
    });

    const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
    const response = await this.fetchImpl(url.toString(), {
      method: options.method || "GET",
      credentials: options.credentials,
      headers: {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        "Accept": "application/json",
        ...(options.headers || {}),
      },
      body: options.body === undefined
        ? undefined
        : isFormData
          ? options.body
          : JSON.stringify(options.body),
    });

    // Read the body once so we can inspect it whether the call succeeded or failed.
    const rawBody = await response.text();
    const contentType = response.headers.get("content-type") || "";
    const isJsonResponse = contentType.includes("application/json");
    let body: unknown = null;

    if (rawBody.trim() !== "") {
      if (isJsonResponse) {
        try {
          body = JSON.parse(rawBody);
        } catch {
          const error = new Error(`Invalid JSON response from ${url.pathname || path}`);
          (error as any).status = response.ok ? 503 : response.status;
          (error as any).code = "api_invalid_json";
          (error as any).details = { status: response.status, contentType };
          throw error;
        }
      } else {
        try {
          body = JSON.parse(rawBody);
        } catch {
          body = rawBody;
        }
      }
    }

    // HTML login/app-shell fallbacks are unavailable APIs, never successful empty data.
    // Do not expose raw HTML or replay a possibly executed write.
    if (typeof body === "string") {
      if (response.status === 401) tryStandbyReauth(getCurrentReturnTo());
      const error = new Error("The service returned an unexpected response. Refresh to retry.");
      (error as any).status = response.ok ? 503 : response.status;
      (error as any).code = "api_unexpected_response";
      (error as any).details = { status: response.status, contentType };
      throw error;
    }

    // A replayed/legacy API error can arrive with HTTP 200; never treat it as success.
    const errorBody = body as Record<string, any> | null;
    if (!response.ok || errorBody?.success === false) {
      if (response.status === 401) {
        tryStandbyReauth(getCurrentReturnTo());
      }

      const fallbackMessage = `HTTP ${response.status}${response.statusText ? `: ${response.statusText}` : ""}`;
      const message =
        errorBody?.error?.message ||
        errorBody?.message ||
        (typeof body === "string" && body.trim() ? body.trim() : fallbackMessage);
      const error = new Error(message);
      (error as any).status = response.ok ? 400 : response.status;
      (error as any).code = errorBody?.error?.code;
      (error as any).details = body;
      throw error;
    }

    return body as T;
  }
}
